import { useCallback, useMemo, useState } from 'react';

import { DocumentType } from '@prisma/client';

import { showSuccessToast } from 'src/components/Toasts/toast';
import { useAdobeSignDocument } from 'src/features/adobeSign/hooks/useAdobeSignDocument';
import {
    getMerchantSepaAgreementColumns,
    getPartnerSepaAgreementColumns,
} from 'src/features/finance/sepaDirectDebits/components/SepaAgreementTables/SepaAgreementTables.utils';
import useDeleteSepaAgreement from 'src/features/finance/sepaDirectDebits/hooks/useDeleteSepaAgreement';
import useRemindSepaAgreement from 'src/features/finance/sepaDirectDebits/hooks/useRemindSepaAgreement';
import { GET_SEPA_DD_MANDATE_AGREEMENTS } from 'src/graphql/queries/getAgreementDocuments.query';
import usePaginatedQuery from 'src/hooks/usePaginatedQuery';
import { SEPA_MANDATE_AGREEMENT_TABLE_TYPE } from 'src/utils/tableUtils';

import { DeleteSepaMandateData, SepaAgreementData, ViewRecordData } from './type';

function useSepaAgreement() {
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState<boolean>(false);

    const {
        getAdobeSignDocumentPdf,
        adobeDocumentPdf,
        isPDFModalOpen,
        setIsPDFModalOpen,
        pdfFile,
        loadingAgreementDocumentPdf,
    } = useAdobeSignDocument();

    const {
        data: merchantSepaMandateData,
        loading: loadingMerchantData,
        paginationMetadata: merchantPaginationMetadata,
        updateSorting: updateMerchantSorting,
        updateFilters: updateMerchantFilters,
        filterState: merchantFilterState,
        fetchMore: fetchMoreMerchant,
        refetch: refetchMerchantData,
        currentSorting: merchantSortState,
    } = usePaginatedQuery({
        query: GET_SEPA_DD_MANDATE_AGREEMENTS,
        filters: {
            entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT,
        },
        pagination: {
            page: 1,
            pageSize: 5,
            pageSizeOptions: [5, 10, 25, 50, 100],
        },
        fetchPolicy: 'cache-and-network',
    });

    const {
        data: partnerSepaMandateData,
        loading: loadingPartnerData,
        paginationMetadata: partnerPaginationMetadata,
        updateSorting: updatePartnerSorting,
        updateFilters: updatePartnerFilters,
        fetchMore: fetchMorePartner,
        refetch: refetchPartnerData,
        filterState: partnerFilterState,
        currentSorting: partnerSortState,
    } = usePaginatedQuery({
        query: GET_SEPA_DD_MANDATE_AGREEMENTS,
        filters: {
            entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER,
        },
        pagination: {
            page: 1,
            pageSize: 5,
            pageSizeOptions: [5, 10, 25, 50, 100],
        },
        fetchPolicy: 'cache-and-network',
    });

    const handleRefetchMerchantData = useCallback(() => {
        updateMerchantFilters({ items: [] });
        refetchMerchantData({
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT,
                page: 1,
                perPage: 5,
            },
            pagination: {
                page: 1,
                pageSize: 5,
                pageSizeOptions: [5, 10, 25, 50, 100],
            },
        });
    }, [refetchMerchantData]);

    const handleRefetchPartnerData = useCallback(() => {
        updatePartnerFilters({ items: [] });
        refetchPartnerData({
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER,
                page: 1,
                perPage: 5,
            },
            pagination: {
                page: 1,
                pageSize: 5,
                pageSizeOptions: [5, 10, 25, 50, 100],
            },
        });
    }, [refetchPartnerData]);

    const handleOnDeleteSepaMandate = useCallback(
        (data: DeleteSepaMandateData) => {
            const deleteSepaMandate = data?.deleteSepaMandate;

            if (!deleteSepaMandate) {
                return;
            }

            setIsDeleteConfirmModalOpen(false);

            const { documentType } = deleteSepaMandate;

            if (documentType === DocumentType.MERCHANT_SEPA_DD) {
                showSuccessToast('Merchant Direct Debit Mandate Cancelled successfully!');
                handleRefetchMerchantData();
            }

            if (documentType === DocumentType.PARTNER_SEPA_DD) {
                showSuccessToast('Partner Direct Debit Mandate Cancelled successfully!');
                handleRefetchPartnerData();
            }
        },
        [handleRefetchMerchantData, handleRefetchPartnerData]
    );

    const { handleDeleteSepaMandate, setIdToDelete } = useDeleteSepaAgreement({
        handleOnDeleteSepaMandate,
    });

    const {
        handleRemindRecord,
        isRemindConfirmModalOpen,
        remindData,
        setIdToRemind,
        setIsRemindConfirmModalOpen,
    } = useRemindSepaAgreement({
        merchantData: merchantSepaMandateData as SepaAgreementData[],
        partnerData: partnerSepaMandateData as SepaAgreementData[],
    });

    const handleViewRecord = useCallback(
        (rowData: ViewRecordData) => {
            const { documentId, agreementId } = rowData;

            getAdobeSignDocumentPdf({ documentId, agreementId });
        },
        [getAdobeSignDocumentPdf]
    );

    const merchantSepaAgreementColumns = useMemo(() => {
        return getMerchantSepaAgreementColumns({
            handleViewRecord,
            setIsRemindConfirmModalOpen,
            setIdToRemind,
            setIdToDelete,
            setIsDeleteConfirmModalOpen,
        });
    }, [
        handleViewRecord,
        setIsRemindConfirmModalOpen,
        setIdToRemind,
        setIdToDelete,
        setIsDeleteConfirmModalOpen,
    ]); // Empty dependancy array to ensure itrs called once

    const partnerSepaAgreementColumns = useMemo(() => {
        return getPartnerSepaAgreementColumns({
            handleViewRecord,
            setIsRemindConfirmModalOpen,
            setIdToRemind,
            setIdToDelete,
            setIsDeleteConfirmModalOpen,
        });
    }, [
        handleViewRecord,
        setIsRemindConfirmModalOpen,
        setIdToRemind,
        setIdToDelete,
        setIsDeleteConfirmModalOpen,
    ]);

    return {
        merchant: {
            merchantData: merchantSepaMandateData,
            loadingMerchantData,
            merchantSepaAgreementColumns,
            refetchMerchantData,
            handleRefetchMerchantData,
            fetchMore: fetchMoreMerchant,
            paginationMetadata: merchantPaginationMetadata,
            updateSorting: updateMerchantSorting,
            updateFilters: updateMerchantFilters,
            filterState: merchantFilterState,
            sortState: merchantSortState,
        },
        partner: {
            partnerData: partnerSepaMandateData,
            loadingPartnerData,
            partnerSepaAgreementColumns,
            refetchPartnerData,
            handleRefetchPartnerData,
            fetchMore: fetchMorePartner,
            paginationMetadata: partnerPaginationMetadata,
            updateSorting: updatePartnerSorting,
            updateFilters: updatePartnerFilters,
            filterState: partnerFilterState,
            sortState: partnerSortState,
        },
        adobeDocumentPdf,
        setIsPDFModalOpen,
        isPDFModalOpen,
        pdfFile,
        loadingAgreementDocumentPdf,
        isRemindConfirmModalOpen,
        setIsRemindConfirmModalOpen,
        handleRemindRecord,
        remindData,
        handleDeleteSepaMandate,
        isDeleteConfirmModalOpen,
        setIsDeleteConfirmModalOpen,
    };
}

export default useSepaAgreement;
